import { useSelector } from 'react-redux'

import { Icons } from '../../../components/Icon'
import {
  formatDeclarationItem,
  fullDeclarationToDeclaration,
} from '../../../helpers/DeclarationHelpers'
import { actions, selectors } from '../../../redux'
import { Child, DeclarationItemState, Member, Partner, Registrant, StepProps } from '../../../types'

export const useSummaryStep = (props: StepProps & { registrantName: string }) => {
  const { t, dispatch, formValues, registrantName } = props
  const familyRelationships = useSelector(selectors.declaration.familyRelationships)

  const resolveRelationshipLabel = (child: Child): string => {
    if (child.familyRelationshipCustom) return child.familyRelationshipCustom
    const match = familyRelationships.find((fr) => fr.id === child.familyRelationshipId)
    return match?.name ?? t('declaration.familyMember')
  }

  const createDeclarationBlock = ({
    declaration,
    type,
    fullName,
    relationshipLabel,
  }: {
    declaration?: Registrant | Partner | Child
    type?: Member
    fullName?: string
    relationshipLabel?: string
  }) => {
    const typeLabel =
      type === 'child'
        ? relationshipLabel ?? t('declaration.familyMember')
        : t(`declaration.${type}`)

    return {
      icon: {
        icon:
          type === 'registrant'
            ? Icons.declarant
            : type === 'partner'
              ? Icons.conjoint
              : Icons.enfant,
      },
      title:
        fullName && fullName?.length > 1
          ? t('declaration.form.declarationList.label', {
              name: fullName,
              type: typeLabel,
            })
          : t('declaration.form.declarationListWithoutName.label', {
              type: typeLabel,
            }),
      declarationTable: {
        numberHeaderLabel: t('declaration.form.addDeclaration.declarationTable.numberHeaderLabel'),
        descriptionHeaderLabel: t(
          'declaration.form.addDeclaration.declarationTable.descriptionHeaderLabel'
        ),
        actionHeaderLabel: t('declaration.form.addDeclaration.declarationTable.actionHeaderLabel'),
        stateHeaderLabel: t('declaration.form.addDeclaration.declarationTable.stateHeaderLabel'),
        refusedStateLabel: t('declaration.form.addDeclaration.declarationTable.refusedStateLabel'),
        validatedStateLabel: t(
          'declaration.form.addDeclaration.declarationTable.validatedStateLabel'
        ),
        pendingStateLabel: t('declaration.form.addDeclaration.declarationTable.pendingStateLabel'),
        editingStateLabel: t('declaration.form.addDeclaration.declarationTable.editingStateLabel'),
        ...(declaration?.items?.length
          ? {
              declarations: declaration.items?.map((item, index) => ({
                id: index + 1,
                description: formatDeclarationItem(t, fullDeclarationToDeclaration(item)),
                state: DeclarationItemState.Validated,
              })),
            }
          : {
              declarations: [
                {
                  id: 1,
                  description:
                    type !== 'registrant'
                      ? t('declaration.form.declarationListWithoutInterestOther.text')
                      : t('declaration.form.declarationListWithoutInterestRegistrant.text'),
                  state: DeclarationItemState.Validated,
                },
              ],
            }),
      },
    }
  }

  return {
    stepPart: {
      text: t('declaration.form.stepPart.summary'),
    },
    form: {
      initialValues: {},
      validationSchema: undefined,
      validateOnChange: false,
    },
    fields: [
      {
        field: createDeclarationBlock({
          declaration: formValues,
          type: 'registrant',
          fullName: registrantName,
        }),
        type: 'declarationBlock',
      },
      {
        field: createDeclarationBlock({
          declaration: formValues.partner,
          type: 'partner',
          fullName: `${formValues.partner?.firstName} ${formValues.partner?.lastName}`,
        }),
        type: 'declarationBlock',
      },
      ...(formValues.children?.length
        ? [
            formValues.children?.map((child: Child) => ({
              field: createDeclarationBlock({
                declaration: child,
                type: 'child',
                fullName: `${child.firstName} ${child.lastName}`,
                relationshipLabel: resolveRelationshipLabel(child),
              }),
              type: 'declarationBlock',
            })),
          ].flat()
        : [
            {
              field: createDeclarationBlock({ type: 'child' }),
              type: 'declarationBlock',
            },
          ]),
    ],
    button: {
      text: t('declaration.form.summary.submit.label'),
      disabled: false,
      onClick: () => {
        dispatch(actions.declaration.setStep(20))
      },
    },
  }
}
